import { ICellRendererParams, ValueSetterParams } from "ag-grid-community";

import {
  QuotationDetailFormRowValues,
  QuotationDetailFormValues,
} from "../../model/form/QuotationDetailForm";
import {
  AgDatePicker,
  agNumberSetter,
  agProductNameFormatter,
} from "../../util/AgGridUtil";
import { isEnglish } from "../../util/ecUtil";
import {
  TranslateFn,
  createDetailNumberFormatter,
  editableCellRule,
  errorCellRule,
  isEmptyQuotationRow,
  renderDetailDeleteButton,
} from "./OrderQuotationUtil";
import { QuotationDetailPermission } from "./QuotationDetailPermission";

/** 列定義の生成に必要な画面状態 */
type Params = {
  tc: TranslateFn;
  permission: QuotationDetailPermission;
  readOnly: boolean;
  bemacFlg: boolean;
  inpQuotation: QuotationDetailFormValues;
  /** 行削除ボタン押下（引数は行の key） */
  onClickDeleteButton: (key: number) => void;
};

/**
 * 見積詳細 明細グリッドの列定義
 */
export function buildQuotationDetailColumnDefs(params: Params) {
  const {
    tc,
    permission,
    readOnly,
    bemacFlg,
    inpQuotation,
    onClickDeleteButton,
  } = params;
  const { editableColumn, editableReplyColumn, showsChangeColor } = permission;

  // 削除ボタンレンダリング
  function rendererDeleteButton(
    rowData: ICellRendererParams<QuotationDetailFormRowValues>
  ) {
    return renderDetailDeleteButton(
      rowData,
      permission.canDeleteRow(rowData),
      onClickDeleteButton
    );
  }

  // 空行・「---」行を空表示にする数値フォーマッタ
  const numberFormatter = createDetailNumberFormatter(isEmptyQuotationRow);
  const unitPriceFormatter = createDetailNumberFormatter(
    isEmptyQuotationRow,
    tc("要問合せ")
  );

  return [
    {
      headerName: tc("明細"),
      children: [
        {
          headerName: "No",
          field: "quotationDetailNo",
          width: 90,
          pinned: true,
          rowDrag: permission.canDragRow(),
          cellClassRules: {
            "b-error-cell": errorCellRule,
          },
        },
        {
          headerName: tc("品目コード"),
          field: "productCode",
          width: 120,
          pinned: true,
          editable: editableColumn,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableColumn),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
        },
      ],
    },
    {
      headerName: "",
      children: [
        {
          headerName: tc("品名"),
          field: "productGroupName",
          cellClassRules: {
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          valueFormatter: agProductNameFormatter,
        },
        {
          headerName: tc("型式"),
          field: "spec",
          editable: editableColumn,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableColumn),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
        },
        {
          headerName: tc("単価"),
          width: 100,
          field: "standardUnitPrice",
          cellClass: "b-grid-cell-multi-line justify-content-end",
          valueFormatter: unitPriceFormatter,
          cellClassRules: {
            "text-start": (params: any) =>
              params.data?.standardUnitPrice === null ||
              typeof params.data?.standardUnitPrice === "undefined",
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
        },
        {
          headerName: tc("依頼数量"),
          width: isEnglish() ? 110 : 100,
          field: "quantity",
          cellStyle: { textAlign: "right" },
          editable: editableColumn,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableColumn),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          valueFormatter: numberFormatter,
          valueSetter: (params: ValueSetterParams) =>
            agNumberSetter(params, "quantity"),
        },
        {
          headerName: tc("単位"),
          width: 100,
          field: "unit",
          editable: editableColumn,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableColumn),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
        },
        {
          headerName: tc("小計"),
          width: 100,
          field: "detailSubTotal",
          cellStyle: { textAlign: "right" },
          cellClassRules: {
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          valueFormatter: numberFormatter,
        },
        {
          headerName: tc("明細備考"),
          field: "note",
          cellClassRules: {
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
        },
        {
          headerName: tc("削除"),
          colId: "deleteByUser",
          width: isEnglish() ? 90 : 80,
          cellRenderer: rendererDeleteButton,
          hide: bemacFlg || readOnly,
          cellClassRules: {
            "b-error-cell": errorCellRule,
          },
        },
      ],
    },
    {
      headerName: tc("BEMAC回答"),
      headerClass: "b-ag-column-reply",
      children: [
        {
          headerName: tc("単価"),
          width: 100,
          field: "replyUnitCost",
          cellStyle: { textAlign: "right" },
          valueFormatter: numberFormatter,
          editable: editableReplyColumn,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableReplyColumn),
            "b-ag-column-reply": (params: any) => !editableReplyColumn(params),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          hide:
            !permission.showsAnswerColumns() || permission.isGeneralEngineer(),
          headerClass: "b-ag-column-reply",
          valueSetter: (params: ValueSetterParams) =>
            agNumberSetter(params, "replyUnitCost"),
        },
        {
          headerName: tc("数量"),
          width: 100,
          field: "replyQuantity",
          cellStyle: { textAlign: "right" },
          editable: editableReplyColumn,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableReplyColumn),
            "b-ag-column-reply": (params: any) => !editableReplyColumn(params),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          hide: !permission.showsAnswerColumns(),
          headerClass: "b-ag-column-reply",
          valueFormatter: numberFormatter,
          valueSetter: (params: ValueSetterParams) =>
            agNumberSetter(params, "replyQuantity"),
        },
        {
          headerName: tc("小計"),
          width: 100,
          field: "replyDetailSubTotal",
          cellClass: "b-ag-column-reply",
          cellStyle: { textAlign: "right" },
          cellClassRules: {
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          valueFormatter: numberFormatter,
          hide:
            !permission.showsAnswerColumns() || permission.isGeneralEngineer(),
          headerClass: "b-ag-column-reply",
        },
        {
          headerName: tc("納期"),
          field: "replyDeliveryDate",
          width: 135,
          cellEditor: AgDatePicker,
          editable: editableReplyColumn,
          cellClassRules: {
            "b-editable-cell": editableCellRule(editableReplyColumn),
            "b-ag-column-reply": (params: any) => !editableReplyColumn(params),
            "b-changed-cell": showsChangeColor,
            "b-error-cell": errorCellRule,
          },
          hide:
            !permission.showsAnswerColumns() ||
            !inpQuotation.replyInstallmentDeliveryFlg,
          headerClass: "b-ag-column-reply",
        },
        {
          headerName: tc("削除"),
          colId: "deleteByBemac",
          width: isEnglish() ? 90 : 80,
          cellRenderer: rendererDeleteButton,
          hide: !bemacFlg || readOnly,
          headerClass: "b-ag-column-reply",
          cellClassRules: {
            "b-error-cell": errorCellRule,
          },
        },
      ],
    },
  ];
}
